import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { SurveyV2Api, SurveyV3Api } from '@trialcard/apigateway.client';
import { toNumber, upperCase } from 'lodash';
import { Container, Resolve } from 'vue-di';
import { SurveySessionValueModel } from '@trialcard/apigateway.models';
import EligibilityModule from './eligibility';
import { FullSurveyResponseModel, FullSurveyModel } from '~/models/survey';
import { Settings } from '~/services/settings';

@InjectModule({ stateFactory: true }, module)
export default class SurveyModule extends InjectVuexModule {
    @Resolve
    settings!: Settings;

    @Resolve
    eligibility!: EligibilityModule;

    @Resolve
    surveyAPI!: SurveyV3Api;

    surveys: { [key: string]: FullSurveyModel } = {};

    sessionId = 0;

    surveyId = '';

    cardTypeQuestionId = '';

    cardTypeAnswerId = '';

    answerValue = '';

    error = false;

    @Action({})
    clearSurvey() {
        this.updateSurveySessionId(0);
        this.setError(false);
    }

    @Action({})
    async loadFullSurvey(surveyId: string) {
        const api = Container.instance.get(SurveyV2Api);
        const response = await api.fullSurveyGet(surveyId);
        const data: FullSurveyResponseModel = response.data;
        this.storeSurvey(data.data);
    }

    @Action({})
    loadLocalFullSurvey(surveyId: string) {
        if (this.surveys[surveyId]) return;
        const surveyFiles = require.context('~/assets/surveys/', true, /(\.json)/);
        const surveyData = surveyFiles.keys().map(z => ({ survey: surveyFiles(z) as FullSurveyModel }));
        for (const { survey } of surveyData) {
            this.storeSurvey(survey);
        }
    }

    @Action({ rawError: true })
    async fetchSurveySession(eligibilityData: SurveySessionValueModel[]) {
        this.setSurveyId(this.settings.surveyId);
        this.setCardTypeQuestionId(this.settings.cardTypeQuestionId);
        this.setCardTypeAnswerId(this.settings.cardTypeAnswerId);

        try {
            const surveySessionResponse = await this.surveyAPI.surveySessionCreate0({ surveyId: Number(this.surveyId) });
            const surveySessionId =
                (surveySessionResponse &&
                    surveySessionResponse.data &&
                    surveySessionResponse.data.data &&
                    surveySessionResponse.data.data.surveySessionId) ||
                null;
            if (surveySessionId) {
                this.updateSurveySessionId(surveySessionId);
                const questionArr = [...eligibilityData];
                // Add cardType question to eligibilityData
                questionArr
                    .filter(item => item.answerValue == null)
                    .push({
                        questionId: Number(this.cardTypeQuestionId),
                        answerId: Number(this.cardTypeAnswerId),
                    } as SurveySessionValueModel);

                // map over eligibilityData and add surveySessionId to all questions
                const sessionValues = questionArr.map(question => ({ ...question, surveySessionId }));
                await this.surveyAPI.fullSurveySessionCreate0({ sessionValues });
            } else {
                throw new Error('Invalid survey session ID');
            }

            this.updateSurveySessionId(Number(surveySessionId));
        } catch (error) {
            this.setError(true);
        }
    }

    getSurveyQuestion(id: string, questionId: string) {
        return this.surveys[id].questionsWithAnswers.find(z => z.questionId === toNumber(questionId)!);
    }

    getSurveyAnswerByCategoryCode(id: string, questionId: string, answerCategoryCode: string) {
        const question = this.getSurveyQuestion(id, questionId);
        if (question && question.answers) {
            const answer = question.answers.find(answer => upperCase(answer.answerCategoryCode) === upperCase(answerCategoryCode));
            return answer;
        }
        return undefined;
    }

    @Mutation
    storeSurvey(model: FullSurveyModel) {
        this.surveys[model.surveyId] = model;
    }

    @Mutation
    updateSurveySessionId(newId: number) {
        this.sessionId = newId;
    }

    @Mutation
    setError(error: boolean) {
        this.error = error;
    }

    @Mutation
    setSurveyId(id = this.settings.surveyId) {
        this.surveyId = id;
    }

    @Mutation setCardTypeQuestionId(questionId: string) {
        this.cardTypeQuestionId = questionId;
    }

    @Mutation setCardTypeAnswerId(answerId: string) {
        this.cardTypeAnswerId = answerId;
    }
}
